import { useEffect, useState } from 'react';
import { DocumentVersion, PromotionDetails, PromotionStatus, PromotionStep, ComparisonResult } from '../types/documentVersion.types';
import { useDocumentAPI } from '../../../DocumentAPI';

interface UseVersionActionsProps {
  documentId: string;
  versions: DocumentVersion[];
  onDocumentUpdate?: () => void;
  onVersionsReload: () => Promise<void>;
  currentVersionId?: string;
}

export interface UseVersionActionsReturn {
  // Version selection
  selectedVersions: string[];
  handleToggleVersionSelection: (versionId: string) => void;
  clearSelectedVersions: () => void;
  
  // Version actions
  handleVersionSelect: (version: DocumentVersion) => void;
  handleDownloadVersion: (version: DocumentVersion) => Promise<void>;
  handleBulkDownload: () => Promise<void>;
  
  // Delete functionality
  showDeleteConfirm: boolean;
  versionToDelete: DocumentVersion | null;
  handleDeleteVersion: (version: DocumentVersion) => void;
  confirmDeleteVersion: () => Promise<void>;
  cancelDeleteVersion: () => void;
  
  // Promotion functionality
  showPromoteConfirm: boolean;
  versionToPromote: DocumentVersion | null;
  promotionDetails: PromotionDetails | null;
  promotionStatus: PromotionStatus;
  promotionStep: PromotionStep;
  promotionError: string | null;
  showSuccessToast: boolean;
  handlePromoteVersion: (version: DocumentVersion) => void;
  confirmPromoteVersion: () => Promise<void>;
  cancelPromoteVersion: () => void;
  
  // Comparison functionality
  comparisonResult: ComparisonResult | null;
  showComparison: boolean;
  handleVersionComparison: () => Promise<void>;
  handleCompareWithActive: (versionId: string, activeVersionId?: string) => void;
  
  // Processing functionality
  isProcessing: boolean;
  processingStatus: any;
  processingNotice: ProcessingNotice | null;
  clearProcessingNotice: () => void;
  handleProcessDocument: () => Promise<void>;
  // Embedding/preprocessing controls
  chunkStrategy: 'fixed' | 'sentence' | 'semantic' | 'legal';
  setChunkStrategy: (s: 'fixed' | 'sentence' | 'semantic' | 'legal') => void;
  chunkSize: number;
  setChunkSize: (n: number) => void;
  chunkOverlap: number;
  setChunkOverlap: (n: number) => void;
  runDefinedTerms: boolean;
  setRunDefinedTerms: (b: boolean) => void;
  runExactSearchIndex: boolean;
  setRunExactSearchIndex: (b: boolean) => void;
  runCaptionPartiesExtractor: boolean;
  setRunCaptionPartiesExtractor: (b: boolean) => void;
  persistCoreFacts: boolean;
  setPersistCoreFacts: (b: boolean) => void;
  
  // Tag management
  documentTags: string[];
  newTag: string;
  setNewTag: (tag: string) => void;
  handleAddTag: () => Promise<void>;
  handleApplyTag: (tag: string) => Promise<void>;
  handleRemoveTag: (tag: string) => Promise<void>;
}

export type ProcessingNotice = {
  tone: 'success' | 'info' | 'warning' | 'error';
  title: string;
  message: string;
  detail?: string;
};

export const useVersionActions = ({
  documentId,
  versions,
  onDocumentUpdate,
  onVersionsReload,
  currentVersionId
}: UseVersionActionsProps): UseVersionActionsReturn => {
  const documentAPI = useDocumentAPI();
  
  // Selection state
  const [selectedVersions, setSelectedVersions] = useState<string[]>([]);
  
  // Delete state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [versionToDelete, setVersionToDelete] = useState<DocumentVersion | null>(null);
  
  // Promotion state
  const [showPromoteConfirm, setShowPromoteConfirm] = useState(false);
  const [versionToPromote, setVersionToPromote] = useState<DocumentVersion | null>(null);
  const [promotionDetails, setPromotionDetails] = useState<PromotionDetails | null>(null);
  const [promotionStatus, setPromotionStatus] = useState<PromotionStatus>('idle');
  const [promotionStep, setPromotionStep] = useState<PromotionStep>('validation');
  const [promotionError, setPromotionError] = useState<string | null>(null);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  
  // Comparison state
  const [comparisonResult, setComparisonResult] = useState<ComparisonResult | null>(null);
  const [showComparison, setShowComparison] = useState(false);
  
  // Processing state
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<any>(null);
  const [processingNotice, setProcessingNotice] = useState<ProcessingNotice | null>(null);
  
  // Tag state
  const [documentTags, setDocumentTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  // Embedding & preprocessing UI state
  const [chunkStrategy, setChunkStrategy] = useState<'fixed' | 'sentence' | 'semantic' | 'legal'>('sentence');
  const [chunkSize, setChunkSize] = useState<number>(1000);
  const [chunkOverlap, setChunkOverlap] = useState<number>(200);
  const [runDefinedTerms, setRunDefinedTerms] = useState<boolean>(false);
  const [runExactSearchIndex, setRunExactSearchIndex] = useState<boolean>(false);
  const [runCaptionPartiesExtractor, setRunCaptionPartiesExtractor] = useState<boolean>(true);
  const [persistCoreFacts, setPersistCoreFacts] = useState<boolean>(true);

  const getErrorMessage = (error: unknown, fallback: string): string => {
    return error instanceof Error && error.message ? error.message : fallback;
  };

  const loadDocumentTags = async () => {
    try {
      const response = await (documentAPI as any).getDocumentTags(documentId);
      setDocumentTags(Array.isArray(response?.document_tags) ? response.document_tags : []);
    } catch (error) {
      console.error('Failed to load document tags:', error);
    }
  };

  useEffect(() => {
    loadDocumentTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, currentVersionId]);

  // Selection handlers
  const handleToggleVersionSelection = (versionId: string) => {
    setSelectedVersions(prev => 
      prev.includes(versionId) 
        ? prev.filter(id => id !== versionId)
        : [...prev, versionId]
    );
  };

  const clearSelectedVersions = () => setSelectedVersions([]);

  // Version handlers
  const handleVersionSelect = (version: DocumentVersion) => {
    // This will be passed from parent component
    console.log('Version selected:', version);
  };

  const buildWorkingCopyFilename = (name?: string | null): string => {
    const fallback = 'EdgeLex-document.docx';
    const safeName = (name || fallback).trim() || fallback;
    return safeName.startsWith('[EDGELEX WORKING COPY]')
      ? safeName
      : `[EDGELEX WORKING COPY] ${safeName}`;
  };

  const handleDownloadVersion = async (version: DocumentVersion) => {
    try {
      const blob = await documentAPI.downloadDocumentBlob(documentId, version.version_id);
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = buildWorkingCopyFilename(version.original_name || `${documentId}-${version.version_number}.docx`);
      window.document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      window.document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading version:', error);
    }
  };

  const handleBulkDownload = async () => {
    if (selectedVersions.length === 0) {
      alert('Please select versions to download');
      return;
    }
    
    for (const versionId of selectedVersions) {
      const version = versions.find(v => v.version_id === versionId);
      if (version) {
        await handleDownloadVersion(version);
      }
    }
  };

  // Delete handlers
  const handleDeleteVersion = (version: DocumentVersion) => {
    setVersionToDelete(version);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteVersion = async () => {
    if (!versionToDelete) return;
    
    try {
      const response = await fetch(`${window.location.origin}/api/v1/dms/documents/${documentId}/versions/${versionToDelete.version_id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${(window as any).keycloak?.token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete version');
      }
      
      // Reload versions
      await onVersionsReload();
      
      // Notify parent component
      onDocumentUpdate?.();
      
      setShowDeleteConfirm(false);
      setVersionToDelete(null);
      alert('Version deleted successfully');
    } catch (error) {
      console.error('Error deleting version:', error);
      alert('Failed to delete version: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const cancelDeleteVersion = () => {
    setShowDeleteConfirm(false);
    setVersionToDelete(null);
  };

  // Promotion handlers
  const handlePromoteVersion = (version: DocumentVersion) => {
    // Find current latest version for comparison (this would need document from parent)
    const currentLatest = versions.find(v => v.version_id === version.version_id); // This logic needs adjustment
    setVersionToPromote(version);
    
    const changeSummary = currentLatest 
      ? `Promoting ${version.version_number} to replace current version as the active version`
      : `Promoting ${version.version_number} to be the active version`;
    
    setPromotionDetails({
      fromVersion: currentLatest || null,
      toVersion: version,
      changeSummary
    });
    
    // Reset promotion state
    setPromotionStatus('idle');
    setPromotionError(null);
    setPromotionStep('validation');
    
    setShowPromoteConfirm(true);
  };

  const confirmPromoteVersion = async () => {
    if (!versionToPromote) return;
    
    try {
      setPromotionStatus('promoting');
      setPromotionStep('validation');
      
      // Step 1 - Validation
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Step 2 - Promotion
      setPromotionStep('promotion');
      
      await documentAPI.promoteVersion(documentId, versionToPromote.version_id);
      
      // Step 3 - Completion
      setPromotionStep('completion');
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Reload versions
      await onVersionsReload();
      
      // Success state
      setPromotionStatus('success');
      setShowSuccessToast(true);
      
      // Notify parent component
      onDocumentUpdate?.();
      
      // Auto-hide success toast after 3 seconds
      setTimeout(() => {
        setShowSuccessToast(false);
        setShowPromoteConfirm(false);
        setVersionToPromote(null);
        setPromotionDetails(null);
        setPromotionStatus('idle');
        setPromotionStep('validation');
      }, 3000);
      
    } catch (error) {
      console.error('Error promoting version:', error);
      
      let errorMessage = 'Failed to promote version';
      if (error instanceof Error) {
        if (error.message.includes('already the latest')) {
          errorMessage = 'This version is already the active version';
        } else if (error.message.includes('not found')) {
          errorMessage = 'Version or document not found';
        } else if (error.message.includes('unauthorized')) {
          errorMessage = 'You do not have permission to promote this version';
        } else {
          errorMessage = error.message;
        }
      }
      
      setPromotionError(errorMessage);
      setPromotionStatus('error');
      
      // Auto-hide error after 5 seconds
      setTimeout(() => {
        setPromotionError(null);
        setPromotionStatus('idle');
        setPromotionStep('validation');
      }, 5000);
    }
  };

  const cancelPromoteVersion = () => {
    setShowPromoteConfirm(false);
    setPromotionStatus('idle');
    setPromotionError(null);
    setPromotionDetails(null);
    setVersionToPromote(null);
  };

  // Comparison handlers
  const handleVersionComparison = async () => {
    if (selectedVersions.length !== 2) {
      setComparisonResult(null);
      return;
    }

    const [version1Id, version2Id] = selectedVersions;
    const v1 = versions.find(v => v.version_id === version1Id);
    const v2 = versions.find(v => v.version_id === version2Id);
    if (!v1 || !v2) {
      setComparisonResult(null);
      return;
    }

    setComparisonResult({
      version1: v1.version_number,
      version2: v2.version_number,
      summary: {
        versionsCompared: [v1.version_number, v2.version_number],
        changeCount: 0,
        wordsAdded: 0,
        wordsRemoved: 0,
      },
      changes: [],
      differences: [],
    });
    setShowComparison(true);
  };

  const handleCompareWithActive = (versionId: string, activeVersionId?: string) => {
    const activeId = activeVersionId || currentVersionId || versions[0]?.version_id;
    if (!activeId || activeId === versionId) {
      setSelectedVersions([versionId]);
      setComparisonResult(null);
      setShowComparison(false);
      return;
    }

    const activeVersion = versions.find(v => v.version_id === activeId);
    const selectedVersion = versions.find(v => v.version_id === versionId);
    setSelectedVersions([activeId, versionId]);
    setComparisonResult({
      version1: activeVersion?.version_number || 'Active',
      version2: selectedVersion?.version_number || 'Selected',
      summary: {
        versionsCompared: [activeVersion?.version_number || 'Active', selectedVersion?.version_number || 'Selected'],
        changeCount: 0,
        wordsAdded: 0,
        wordsRemoved: 0,
      },
      changes: [],
      differences: [],
    });
    setShowComparison(true);
  };

  // Processing handlers
  const handleProcessDocument = async () => {
    try {
      setIsProcessing(true);
      setProcessingNotice(null);
      const response = await (documentAPI as any).processDocument(documentId, {
        ai_tasks: {
          ocr: true,
          classification: true,
          citation_extraction: true,
          risk_assessment: true,
        },
      });
      
      setProcessingStatus(response);
      setProcessingNotice({
        tone: 'success',
        title: 'Document processing queued',
        message: response?.message || 'This document version was queued for EdgeLex document processing.',
      });
    } catch (error) {
      console.error('Error queueing document processing:', error);
      setProcessingStatus(null);
      setProcessingNotice({
        tone: 'error',
        title: 'Document processing was not queued',
        message: getErrorMessage(error, 'Failed to queue document processing.'),
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Tag handlers
  const handleAddTag = async () => {
    if (!newTag.trim()) return;
    await handleApplyTag(newTag.trim(), true);
  };

  const handleApplyTag = async (tag: string, clearInput: boolean = false) => {
    const normalizedTag = tag.trim();
    if (!normalizedTag || documentTags.some(existingTag => existingTag.toLowerCase() === normalizedTag.toLowerCase())) {
      if (clearInput) setNewTag('');
      return;
    }
    
    try {
      const response = await documentAPI.manageDocumentTags(documentId, 'add', [normalizedTag]);
      setDocumentTags(response.document_tags);
      setProcessingNotice({
        tone: 'success',
        title: 'Tag applied',
        message: `"${normalizedTag}" is now available for DMS filtering and AI retrieval metadata.`,
      });
      if (clearInput) setNewTag('');
    } catch (error) {
      console.error('Error adding tag:', error);
      setProcessingNotice({
        tone: 'error',
        title: 'Tag was not applied',
        message: getErrorMessage(error, 'Failed to add tag.'),
      });
    }
  };

  const handleRemoveTag = async (tag: string) => {
    try {
      const response = await documentAPI.manageDocumentTags(documentId, 'remove', [tag]);
      setDocumentTags(response.document_tags);
      setProcessingNotice({
        tone: 'info',
        title: 'Tag removed',
        message: `"${tag}" was removed from this document.`,
      });
    } catch (error) {
      console.error('Error removing tag:', error);
      setProcessingNotice({
        tone: 'error',
        title: 'Tag was not removed',
        message: getErrorMessage(error, 'Failed to remove tag.'),
      });
    }
  };

  return {
    // Selection
    selectedVersions,
    handleToggleVersionSelection,
    clearSelectedVersions,
    
    // Version actions
    handleVersionSelect,
    handleDownloadVersion,
    handleBulkDownload,
    
    // Delete
    showDeleteConfirm,
    versionToDelete,
    handleDeleteVersion,
    confirmDeleteVersion,
    cancelDeleteVersion,
    
    // Promotion
    showPromoteConfirm,
    versionToPromote,
    promotionDetails,
    promotionStatus,
    promotionStep,
    promotionError,
    showSuccessToast,
    handlePromoteVersion,
    confirmPromoteVersion,
    cancelPromoteVersion,
    
    // Comparison
    comparisonResult,
    showComparison,
    handleVersionComparison,
    handleCompareWithActive,
    
    // Processing
    isProcessing,
    processingStatus,
    processingNotice,
    clearProcessingNotice: () => setProcessingNotice(null),
    handleProcessDocument,
    // Embedding/preprocessing controls
    chunkStrategy,
    setChunkStrategy,
    chunkSize,
    setChunkSize,
    chunkOverlap,
    setChunkOverlap,
    runDefinedTerms,
    setRunDefinedTerms,
    runExactSearchIndex,
    setRunExactSearchIndex,
    runCaptionPartiesExtractor,
    setRunCaptionPartiesExtractor,
    persistCoreFacts,
    setPersistCoreFacts,
    
    // Tags
    documentTags,
    newTag,
    setNewTag,
    handleAddTag,
    handleApplyTag,
    handleRemoveTag
  };
};
